// src/pages/chat/CTA/cta.tsx
import React from 'react';
import { useRecordingCtaButton } from './cta.hooks';
import { Btn, VideoStack, VideoEl } from './cta.styled';
import { ENVIRONMENT } from '../../../env';

const DEV = ENVIRONMENT === 'DEV';

// Transparent 1x1 PNG — suppresses the Android WebView default video placeholder
const TRANSPARENT_POSTER =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
const log = (...args: any[]) => {
  if (DEV) console.log('[cta.tsx]', ...args);
};

// ✅ phases aligned to your exact new model
export type Phase =
  | 'restLoop'
  | 'toRecording'
  | 'recording'
  | 'toPaused'
  | 'paused'
  | 'toResume'
  | 'toStop';

export type RecordingCtaButtonProps = {
  className?: string;
  disabled?: boolean;

  onStartRecording?: () => void;
  onStopRecording?: () => void;
  onReturnToRest?: () => void;
  onPhaseChange?: (phase: Phase) => void;

  // ✅ NEW: send audio + transcript on stop
  onSendRecording?: ((audioBlob: Blob, transcript?: string) => void) | undefined;

  // ✅ Error callback when recording fails (e.g. no audio captured)
  onRecordingFailed?: ((message: string) => void) | undefined;

  defaultRecording?: boolean;
  ariaLabel?: string;

  videos: {
    pauseOff: string;
    pauseOn: string;
    recordingLoop: string;
    restToRecording: string;
    restLoop: string;
    stopRecordingFromPause: string;
    stopRecordingFromRecording: string;
  };
};

export type CtaHandle = {
  start: () => void;
  stop: () => void;
  cancel: () => void;
  pause: () => void;
  resume: () => void;

  // ✅ NEW: discard + restart recording (stays in the recording flow; no send)
  reRecord: () => void;

  /** convenience for UI */
  getPhase: () => Phase;
};

export const CTA = React.forwardRef<CtaHandle, RecordingCtaButtonProps>(function CTA(
  {
    className,
    disabled,
    onStartRecording,
    onStopRecording,
    onReturnToRest,
    onPhaseChange,
    onSendRecording,
    onRecordingFailed,
    defaultRecording = false,
    ariaLabel = 'Recording button',
    videos,
  },
  ref
) {
  const {
    isRecording,
    isPaused,
    isBusyTransition,
    handlePress,
    activeIndex,
    v0,
    v1,
    phase,
    start,
    stop,
    cancel,
    pause,
    resume,
    reRecord,
    getPhase,
  } = useRecordingCtaButton({
    disabled: !!disabled,
    defaultRecording,
    videos,
    ...(onStartRecording && { onStartRecording }),
    ...(onStopRecording && { onStopRecording }),
    ...(onReturnToRest && { onReturnToRest }),
    ...(onPhaseChange && { onPhaseChange }),
    ...(onSendRecording && { onSendRecording }),
    ...(onRecordingFailed && { onRecordingFailed }),
  });

  React.useImperativeHandle(
    ref,
    () => ({
      start,
      stop,
      cancel,
      pause,
      resume,
      reRecord,
      getPhase,
    }),
    [start, stop, cancel, pause, resume, reRecord, getPhase]
  );

  React.useEffect(() => {
    log(
      'phase=',
      phase,
      'isRecording=',
      isRecording,
      'isPaused=',
      isPaused,
      'busy=',
      isBusyTransition,
      'activeIndex=',
      activeIndex
    );
  }, [phase, isRecording, isPaused, isBusyTransition, activeIndex]);

  return (
    <Btn
      type="button"
      className={className}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={isRecording}
      data-recording={isRecording ? 'true' : 'false'}
      data-paused={isPaused ? 'true' : 'false'}
      data-busy={isBusyTransition ? 'true' : 'false'}
      onPointerDown={(e) => {
        e.stopPropagation();

        // ✅ Reduce accidental taps: only accept presses inside a smaller "hit circle"
        // This behaves like a mask without changing layout/styling.
        try {
          const el = e.currentTarget as HTMLElement;
          const r = el.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
          const x = (e as any).clientX ?? 0;
          const y = (e as any).clientY ?? 0;
          const dx = x - cx;
          const dy = y - cy;

          const baseRadius = Math.min(r.width, r.height) / 2;
          const radius = Math.max(0, baseRadius - 10); // tighter tap area
          if (dx * dx + dy * dy > radius * radius) return;
        } catch {}

        handlePress(e);
      }}
    >
      <VideoStack>
        <VideoEl
          ref={v0.ref}
          data-active={activeIndex === 0 ? 'true' : 'false'}
          playsInline
          muted
          preload="auto"
          poster={TRANSPARENT_POSTER}
        />
        <VideoEl
          ref={v1.ref}
          data-active={activeIndex === 1 ? 'true' : 'false'}
          playsInline
          muted
          preload="auto"
          poster={TRANSPARENT_POSTER}
        />
      </VideoStack>
    </Btn>
  );
});

export default CTA;
